import React, { useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import ExplanationBox from '../shared/ExplanationBox';
import Tooltip from '../shared/Tooltip';

const Step1A_PhilosophicalDimensions = () => {
  const { data, updateData } = useWizard();

  const dimensions = data.philosophicalDimensions || {
    ontology: '',
    epistemology: '',
    axiology: ''
  };

  const updateDimension = (dimension, value) => {
    updateData('philosophicalDimensions', {
      ...dimensions,
      [dimension]: value
    });
  };

  const ontologyOptions = [
    {
      value: 'realist',
      label: 'Realism',
      description: 'Reality exists independently of human perception. There is one true reality "out there" waiting to be discovered.',
      example: 'The physical world exists whether or not humans observe it. Poverty has objective, measurable characteristics.',
      paradigmHint: ['positivism', 'critical_realism']
    },
    {
      value: 'relativist',
      label: 'Relativism',
      description: 'Multiple realities exist, constructed through social interaction. Reality is subjective and context-dependent.',
      example: '"Success" means different things to different people. Mental health is understood differently across cultures.',
      paradigmHint: ['interpretivism', 'transformative']
    },
    {
      value: 'pragmatic',
      label: 'Pragmatic',
      description: 'Reality is whatever is useful for solving the problem at hand. Both objective and subjective perspectives have value.',
      example: 'Whether to use objective or subjective measures depends on what will best answer the research question.',
      paradigmHint: ['pragmatism']
    }
  ];

  const epistemologyOptions = [
    {
      value: 'objectivist',
      label: 'Objectivism',
      description: 'Knowledge comes from direct observation and measurement. Facts are separate from values. Researcher remains detached.',
      example: 'Valid knowledge requires empirical evidence that any observer could verify independently.',
      paradigmHint: ['positivism']
    },
    {
      value: 'subjectivist',
      label: 'Subjectivism',
      description: 'Knowledge is co-constructed between researcher and participants. Understanding comes from interpretation of meanings.',
      example: 'To understand patient experiences, researchers must engage with their subjective perspectives.',
      paradigmHint: ['interpretivism', 'transformative']
    },
    {
      value: 'critical',
      label: 'Critical / Stratified',
      description: 'Knowledge involves uncovering hidden mechanisms beneath observable reality. Both empirical and interpretive approaches needed.',
      example: 'Understanding inequality requires examining both statistical patterns and underlying power structures.',
      paradigmHint: ['critical_realism', 'transformative']
    },
    {
      value: 'practical',
      label: 'Practical Knowledge',
      description: 'Knowledge is what helps us act effectively. Multiple ways of knowing are valid if they solve problems.',
      example: 'If combining methods produces actionable insights, that approach generates valid knowledge.',
      paradigmHint: ['pragmatism']
    }
  ];

  const axiologyOptions = [
    {
      value: 'value_free',
      label: 'Value-Free',
      description: 'Research should be objective and free from researcher values. Facts must be separated from values.',
      example: 'The researcher strives to remain neutral and not let personal beliefs influence findings.',
      paradigmHint: ['positivism']
    },
    {
      value: 'value_laden',
      label: 'Value-Laden',
      description: 'Research is inherently influenced by values. Researchers should acknowledge and reflect on their biases.',
      example: 'The researcher openly discusses how their background and perspective shaped the interpretation.',
      paradigmHint: ['interpretivism', 'critical_realism']
    },
    {
      value: 'value_driven',
      label: 'Value-Driven',
      description: 'Research should explicitly pursue values like social justice, empowerment, and equity.',
      example: 'The research aims to challenge inequalities and advocate for marginalized communities.',
      paradigmHint: ['transformative']
    },
    {
      value: 'pragmatic_values',
      label: 'Pragmatic',
      description: 'Values guide research problem selection but should not bias interpretation. Focus on practical outcomes.',
      example: 'Researcher values drive the choice of problem but findings are evaluated by their practical usefulness.',
      paradigmHint: ['pragmatism']
    }
  ];

  const getRecommendedParadigm = () => {
    const { ontology, epistemology, axiology } = dimensions;
    
    if (!ontology || !epistemology || !axiology) return null;

    const scores = {
      positivism: 0,
      interpretivism: 0,
      pragmatism: 0,
      critical_realism: 0,
      transformative: 0
    };

    const ontologyOption = ontologyOptions.find(o => o.value === ontology);
    const epistemologyOption = epistemologyOptions.find(o => o.value === epistemology);
    const axiologyOption = axiologyOptions.find(o => o.value === axiology);

    if (ontologyOption) {
      ontologyOption.paradigmHint.forEach(p => scores[p] += 2);
    }
    if (epistemologyOption) {
      epistemologyOption.paradigmHint.forEach(p => scores[p] += 2);
    }
    if (axiologyOption) {
      axiologyOption.paradigmHint.forEach(p => scores[p] += 2);
    }

    const maxScore = Math.max(...Object.values(scores));
    const topParadigms = Object.entries(scores)
      .filter(([_, score]) => score === maxScore && score > 0)
      .map(([paradigm, _]) => paradigm);

    return {
      recommended: topParadigms[0] || null,
      alternatives: topParadigms.slice(1),
      scores
    };
  };

  const paradigmLabels = {
    positivism: 'Positivism',
    interpretivism: 'Interpretivism',
    pragmatism: 'Pragmatism',
    critical_realism: 'Critical Realism',
    transformative: 'Transformative'
  };

  const recommendation = getRecommendedParadigm();

  useEffect(() => {
    if (recommendation?.recommended) {
      updateData('recommendedParadigm', recommendation.recommended);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.ontology, dimensions.epistemology, dimensions.axiology]);

  const canProceed = dimensions.ontology && dimensions.epistemology && dimensions.axiology;

  const renderOptionCards = (options, selectedValue, dimension, title, tooltipText) => (
    <div className="dimension-section">
      <h3 className="dimension-title">
        {title}
        <Tooltip text={tooltipText} />
      </h3>
      <div className="option-cards dimension-cards">
        {options.map((option, index) => (
          <div
            key={option.value}
            className={`option-card ${selectedValue === option.value ? 'selected' : ''} ${index < options.length - 1 ? 'with-separator' : ''}`}
            onClick={() => updateDimension(dimension, option.value)}
          >
            <div className="option-header">
              <input
                type="radio"
                name={dimension}
                value={option.value}
                checked={selectedValue === option.value}
                onChange={() => updateDimension(dimension, option.value)}
              />
              <span className="option-title"><strong>{option.label}</strong></span>
            </div>
            <p className="option-description">
              <strong>What it means:</strong> {option.description}
            </p>
            <p className="option-description" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
              <strong>Example:</strong> {option.example}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="wizard-step">
      <h2 className="step-title">Step 2: Philosophical Dimensions</h2>
      <p className="step-description">
        Before choosing a research paradigm, answer these three foundational questions about your beliefs regarding reality, knowledge, and values.
        <Tooltip text="These three philosophical dimensions (ontology, epistemology, axiology) form the foundation of your research worldview and help determine which paradigm best fits your approach." />
      </p>

      <ExplanationBox title="Why This Matters">
        <p>Every research project is built on philosophical assumptions, whether explicit or implicit. By clarifying your position on these three dimensions, you will:</p>
        <ul>
          <li>Ensure internal consistency in your research design</li>
          <li>Better understand which paradigm aligns with your beliefs</li>
          <li>Be prepared to defend your methodological choices</li>
        </ul>
      </ExplanationBox>

      {renderOptionCards(
        ontologyOptions,
        dimensions.ontology,
        'ontology',
        'Ontology: What is the nature of reality?',
        'Ontology is the study of being and existence. It asks: What is real? Does reality exist independently of our minds, or is it constructed through our perceptions and social interactions?'
      )}

      {renderOptionCards(
        epistemologyOptions,
        dimensions.epistemology,
        'epistemology',
        'Epistemology: How can we know what we know?',
        'Epistemology is the study of knowledge. It asks: What counts as valid knowledge? How should we acquire knowledge? What is the relationship between the researcher and what is being researched?'
      )}

      {renderOptionCards(
        axiologyOptions,
        dimensions.axiology,
        'axiology',
        'Axiology: What is the role of values in research?',
        'Axiology is the study of values. It asks: Should research be value-free or value-driven? How do the researcher\'s values and ethics influence the research process and outcomes?'
      )}

      {recommendation?.recommended && (
        <div className="recommendation-box">
          <h3>Based on Your Selections</h3>
          <div className="recommendation-content">
            <p>
              <strong>Recommended Paradigm:</strong>{' '}
              <span className="recommended-paradigm">{paradigmLabels[recommendation.recommended]}</span>
            </p>
            {recommendation.alternatives.length > 0 && (
              <p>
                <strong>Also Compatible:</strong>{' '}
                {recommendation.alternatives.map(p => paradigmLabels[p]).join(', ')}
              </p>
            )}
            <p className="recommendation-explanation">
              {getRecommendationExplanation(dimensions)}
            </p>
          </div>
        </div>
      )}

      <NavigationButtons
        canProceed={canProceed}
        proceedMessage="Please select an option for each philosophical dimension before proceeding."
      />
    </div>
  );
};

const getRecommendationExplanation = (dimensions) => {
  const { ontology, epistemology, axiology } = dimensions;

  if (ontology === 'realist' && epistemology === 'objectivist' && axiology === 'value_free') {
    return 'Your selections strongly align with Positivism. You believe in an objective reality that can be measured, knowledge through empirical observation, and value-free research.';
  }

  if (ontology === 'relativist' && epistemology === 'subjectivist' && axiology === 'value_laden') {
    return 'Your selections strongly align with Interpretivism. You believe reality is socially constructed, knowledge comes from understanding meanings, and research is inherently influenced by values.';
  }

  if (ontology === 'pragmatic' && epistemology === 'practical' && axiology === 'pragmatic_values') {
    return 'Your selections strongly align with Pragmatism. You value practical outcomes, believe multiple ways of knowing are valid, and focus on what works to solve research problems.';
  }

  if ((ontology === 'realist' || ontology === 'pragmatic') && epistemology === 'critical') {
    return 'Your selections align with Critical Realism. You believe reality exists independently but is understood through interpretive processes that uncover underlying mechanisms.';
  }

  if (axiology === 'value_driven' || (epistemology === 'critical' && axiology === 'value_laden')) {
    return 'Your selections align with the Transformative paradigm. You believe research should explicitly address power imbalances and advocate for social justice.';
  }

  return 'Your selections suggest a combination of philosophical perspectives. In the next step, you can choose a paradigm that best represents your research approach, or you may override the recommendation.';
};

export default Step1A_PhilosophicalDimensions;
